import React from 'react';
import {View, Text, TextInput} from 'react-native';
import styles from '../../style/costCalculator';

/**
 * A single labeled numeric input for the cost calculator. Kept deliberately dumb — it
 * reports raw string input up via `onChangeValue` and lets the screen decide how to
 * parse/derive linked fields, so the two-way reciprocal-pair logic stays in one place
 * (costCalculatorUtils.deriveLinkedFieldUpdates) rather than duplicated per field.
 *
 * `isCurrency`: renders a `$` prefix and switches the virtual keyboard to `decimal-pad`.
 * This matters beyond cosmetics — react-native-web maps `keyboardType="numeric"` to
 * `inputMode="numeric"`, which on a real mobile virtual keyboard has NO decimal point key,
 * so a plain numeric field cannot enter cents at all on a phone. `decimal-pad` maps to
 * `inputMode="decimal"`, which does include one.
 *
 * `description`: an optional one-line "what this field means" caption rendered under the
 * label, styled the same way as `SectionHeader`'s description text (muted, regular weight)
 * so a field-level explanation reads consistently with the section-level ones. Distinct from
 * `hint`, which is reserved for the smaller "linked to the field below" cross-reference note
 * rendered under the input.
 */
const NumberField = ({label, value, onChangeValue, hint, testID, suffix, isCurrency, description}) => {
  return (
    <View style={styles.field}>
      <Text style={styles.fieldLabel}>
        {label}
        {suffix ? ` (${suffix})` : ''}
      </Text>
      {description ? <Text style={styles.fieldDescription}>{description}</Text> : null}
      <View style={isCurrency ? styles.inputWithPrefix : undefined}>
        {isCurrency ? <Text style={styles.inputPrefixText}>$</Text> : null}
        <TextInput
          testID={testID}
          style={[styles.input, isCurrency && styles.inputWithPrefixPadding]}
          keyboardType={isCurrency ? 'decimal-pad' : 'numeric'}
          value={value === null || value === undefined ? '' : String(value)}
          onChangeText={(text) => onChangeValue(text === '' ? '' : Number(text))}
        />
      </View>
      {hint ? <Text style={styles.reciprocalHint}>{hint}</Text> : null}
    </View>
  );
};

export default NumberField;
